import { DropOverMenu, Icon } from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	ReactElement,
	useCallback,
	useState
} from "react";
import { HubItemMediaKind } from "client/Api/Hub/common";
import { MenuList } from "../Builder/Layout/Menus/MenuList";
import { EmbedInputDialogData } from "./EmbedInputDialog";
import { generateTrackingId } from "client/Misc/utils";
import { VideoInputDialogData } from "./VideoInputDialog";
import { HubItemMediaFormModel } from "../Management/common";
import { never } from "client_legacy/ndaw/errors";
import { SelectImageMenu } from "../Builder/Layout/Menus/SelectImageMenu";
import { HubImageSelectorKind } from "../Management/useHubImageSelector";

export type HubMediaSelectMenuProps = {
	value: HubItemMediaFormModel;
	onChange: (newData: HubItemMediaFormModel) => void;
	target: HTMLElement;
	onClose: () => void;
	onOpenEmbedInputDialog: (data: EmbedInputDialogData) => Promise<TryResult<string>>;
	onOpenVideoInputDialog: (data: VideoInputDialogData) => Promise<TryResult<string>>;
	onSelectImage: (kind: HubImageSelectorKind) => void;
	trackingId: string;
	isPixabayImageGalleryAvailable: boolean;
};

export const HubMediaSelectMenu = (props: HubMediaSelectMenuProps): ReactElement => {
	const {
		value,
		target,
		onClose,
		onOpenEmbedInputDialog,
		onOpenVideoInputDialog,
		onSelectImage,
		onChange,
		trackingId,
		isPixabayImageGalleryAvailable
	} = props;

	const [view, setView] = useState<ViewState>("default");

	const selectImageHandler = useCallback(
		(kind: HubImageSelectorKind): void => {
			onClose();

			onSelectImage(kind);
		},
		[onClose, onSelectImage]
	);

	const selectVideoHandler = useCallback(
		async (): Promise<void> => {
			onClose();

			const currentVideoUrl: string = value.mediaKind === HubItemMediaKind.Video
				? value.videoUrl
				: "";

			const videoResult: TryResult<string> = await onOpenVideoInputDialog({
				videoUrl: currentVideoUrl
			});

			if (videoResult.wasSuccessful) {
				onChange({
					mediaKind: HubItemMediaKind.Video,
					videoUrl: videoResult.result
				});
			}
		},
		[
			onClose,
			value,
			onOpenVideoInputDialog,
			onChange
		]
	);

	const selectEmbedHandler = useCallback(
		async (): Promise<void> => {
			onClose();

			const currentEmbedCode: string = value.mediaKind === HubItemMediaKind.Embed
				? value.embedCode
				: "";

			const embedResult: TryResult<string> = await onOpenEmbedInputDialog({
				code: currentEmbedCode
			});

			if (embedResult.wasSuccessful) {
				onChange({
					mediaKind: HubItemMediaKind.Embed,
					embedCode: embedResult.result
				});
			}
		},
		[
			onOpenEmbedInputDialog,
			value,
			onChange,
			onClose
		]
	);

	const renderView = (): ReactElement => {
		switch (view) {
			case "default":
				return (
					<MenuList trackingId={trackingId}>
						<MenuList.Item
							image={<Icon value="image" size="large" />}
							title={localize("_Image")}
							trackingId="image"
							onClick={() => setView("image-source")}
							index={0}
						/>
						<MenuList.Item
							image={<Icon value="video" size="large" />}
							title={localize("_Video")}
							trackingId="video"
							onClick={selectVideoHandler}
							index={1}
						/>
						<MenuList.Item
							image={<Icon value="code" size="large" />}
							title={localize("_Embed")}
							trackingId="embed"
							onClick={selectEmbedHandler}
							index={2}
						/>
					</MenuList>
				);
			case "image-source":
				return (
					<SelectImageMenu
						trackingId="select-image-menu"
						onBack={() => setView("default")}
						title={localize("_SelectImage")}
						onSelectImage={selectImageHandler}
						isPixabayImageGalleryAvailable={isPixabayImageGalleryAvailable}
					/>
				);
			default:
				never(view);
		}
	};

	return (
		<DropOverMenu
			target={target}
			onClose={onClose}
			title={localize(
				value.mediaKind === HubItemMediaKind.None
					? "_Add"
					: "_Edit"
			)}
			trackingId={generateTrackingId(trackingId, "__wrapper")}
		>
			{renderView()}
		</DropOverMenu>
	);
};
HubMediaSelectMenu.displayName = "HubMediaSelectMenu";

type ViewState =
	| "default"
	| "image-source";